
import React, { useState, KeyboardEvent, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PlusCircle, GripVertical, AlertCircle, Trash2, Link, Upload, FileText } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { FormSection, FormStep } from "@/models";
import { formFieldApi, formSectionApi } from "@/services/api";
import { supabase } from "@/integrations/supabase/client";
import { newObjectName } from "@/utils/storageRefs";
import { 
  Alert, 
  AlertDescription 
} from "@/components/ui/alert";
import FormFieldComponent from "./FormField";
import SimpleAddFieldDialog from "./dialogs/SimpleAddFieldDialog";

interface FormSectionProps {
  section: FormSection;
  onSectionUpdated: () => Promise<void>;
  onDeleteSection: (sectionId: number) => Promise<void>;
  onEditSection: (section: FormSection) => void;
  isParentSubmitting?: boolean;
  step?: FormStep;
  index?: number;
  dragHandleProps?: any;
}

const FormSectionComponent = ({
  section,
  onSectionUpdated,
  onDeleteSection,
  onEditSection,
  isParentSubmitting = false,
  dragHandleProps
}: FormSectionProps) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usefulLinks, setUsefulLinks] = useState(section.useful_links || []);
  const [newLinkLabel, setNewLinkLabel] = useState("");
  const [newLinkUrl, setNewLinkUrl] = useState("");
  const [isAddingLink, setIsAddingLink] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  console.log(`FormSection rendering for section ${section.id} - isDialogOpen: ${isDialogOpen}`);

  const handleAddField = async (data: {
    label: string;
    fieldType: string;
    isRequired: boolean;
    filledBy: string;
    content?: string;
    name?: string;
    options?: any[];
    dataType?: string;
    mainApplicantOnly?: boolean;
  }) => {
    try {
      console.log("Starting field creation process", data);
      setIsSubmitting(true);
      setError(null);
      
      // Construct field data
      const fieldData: any = {
        form_section_id: section.id,
        field_type: data.fieldType,
        label: data.label,
        is_required: data.isRequired,
        name: data.name,
        filled_by: data.filledBy,
        // The dialog has always collected dataType but it was never forwarded,
        // so every new field silently defaulted to 'generic'. Fixed here.
        data_type: data.dataType || 'generic',
      };

      // Multi-applicant signatures (D1): only meaningful for signature/initials.
      if (data.fieldType === 'signature' || data.fieldType === 'initials') {
        fieldData.main_applicant_only = data.mainApplicantOnly === true;
      }

      // Add content for information type
      if (data.fieldType === 'information' && data.content) {
        fieldData.content = data.content;
      }

      // Add options for select type
      if (data.fieldType === 'select' && data.options && data.options.length > 0) {
        fieldData.options = data.options;
      }
      
      console.log("Sending field data to API:", fieldData);
      
      // Create the field
      await formFieldApi.create(fieldData);
      
      toast({
        title: "Field added",
        description: "The field has been added successfully",
      });
      
      // Close the dialog and refresh the section
      setIsDialogOpen(false);
      await onSectionUpdated();
      
    } catch (error: any) {
      console.error("Error adding field:", error);
      setError(error.message || "An error occurred while adding the field");
      toast({
        variant: "destructive",
        title: "Error adding field",
        description: error.message || "An error occurred while adding the field",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const isDisabled = isSubmitting || isParentSubmitting;

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent form submission
      addUsefulLink();
    }
  };

  // Extract filename from URL for auto-filling label
  const extractFilenameFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const filename = pathname.split('/').pop() || '';
      // Remove extension and clean up
      const nameWithoutExt = filename.replace(/\.[^/.]+$/, "");
      // Remove timestamp prefix if present (e.g., "1234567890_filename" -> "filename")
      const cleanedName = nameWithoutExt.replace(/^\d+_/, '');
      return decodeURIComponent(cleanedName);
    } catch {
      return '';
    }
  };

  const handleUrlChange = (url: string) => {
    setNewLinkUrl(url);
    // Auto-fill label from URL if label is empty
    if (!newLinkLabel.trim() && url.trim()) {
      const extractedName = extractFilenameFromUrl(url);
      if (extractedName) {
        setNewLinkLabel(extractedName);
      }
    }
  };

  const addUsefulLink = async () => {
    if (!newLinkUrl.trim()) {
      toast({
        variant: "destructive",
        title: "Invalid Link",
        description: "Please provide a URL or upload a file",
      });
      return;
    }

    // Use URL filename as label if label is empty
    const finalLabel = newLinkLabel.trim() || extractFilenameFromUrl(newLinkUrl) || 'Link';

    try {
      setIsSubmitting(true);

      const newLinks = [
        ...usefulLinks,
        { label: finalLabel, url: newLinkUrl.trim() }
      ];

      await formSectionApi.update(section.id, {
        useful_links: newLinks
      });

      setUsefulLinks(newLinks);
      setNewLinkLabel("");
      setNewLinkUrl("");
      setIsAddingLink(false);

      await onSectionUpdated();

      toast({
        title: "Link Added",
        description: "Useful link has been added successfully",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error Adding Link",
        description: error.message || "Failed to add useful link",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const removeUsefulLink = async (indexToRemove: number) => {
    try {
      setIsSubmitting(true);

      const newLinks = usefulLinks.filter((_, index) => index !== indexToRemove);

      await formSectionApi.update(section.id, {
        useful_links: newLinks
      });

      // Update local state immediately instead of triggering a full refresh
      setUsefulLinks(newLinks);

      toast({
        title: "Link Removed",
        description: "Useful link has been removed successfully",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error Removing Link",
        description: error.message || "Failed to remove useful link",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);

      // Non-guessable name. migrated-files stays a PUBLIC bucket — these are
      // help documents shown to buyers who may not have an account yet — so
      // the row keeps a public URL, but the name is a UUID, not a timestamp
      // plus the original file name.
      const filePath = `useful-links/${section.id}/${newObjectName(file.name)}`;

      // Upload file to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from('migrated-files')
        .upload(filePath, file);

      if (uploadError) {
        throw new Error(uploadError.message);
      }

      // Get the public URL
      const { data: urlData } = supabase.storage
        .from('migrated-files')
        .getPublicUrl(filePath);

      const publicUrl = urlData.publicUrl;

      // Set the URL field with the uploaded file's URL
      setNewLinkUrl(publicUrl);

      // Auto-fill label with filename (without extension) if empty
      if (!newLinkLabel.trim()) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
        setNewLinkLabel(nameWithoutExt);
      }

      toast({
        title: "File Uploaded",
        description: "File uploaded successfully. Click the add button to save.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error Uploading File",
        description: error.message || "Failed to upload file",
      });
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const cancelAddLink = () => {
    setIsAddingLink(false);
    setNewLinkLabel("");
    setNewLinkUrl("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Check if a URL is from the migrated-files bucket (uploaded file)
  const isUploadedFile = (url: string) => {
    return url.includes('migrated-files');
  };

  if (error) {
    return (
      <Alert variant="destructive" className="mb-4">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="border rounded-lg mb-6 bg-background">
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center space-x-2">
          <div {...dragHandleProps} className="cursor-grab active:cursor-grabbing">
            <GripVertical className="h-5 w-5 text-muted-foreground" />
          </div>
          <div>
            <h3 className="text-lg font-medium">
              {section.section_title || `Section ${section.section_order + 1}`}
            </h3>
            {section.section_subtitle && (
              <p className="text-sm text-muted-foreground">
                {section.section_subtitle}
              </p>
            )}
          </div>
          <div className="flex flex-col space-y-1">
            <div className="flex space-x-1">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                {section.section_type}
              </span>
              {section.multiple_elements && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                  Multiple Elements
                </span>
              )}
            </div>
            {section.multiple_elements && section.min_quantity !== undefined && section.min_quantity !== null && (
              <div className="text-[11px] text-gray-500 pl-2">
                Minimum required: {section.min_quantity}
              </div>
            )}
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            type="button"
            onClick={() => onEditSection(section)}
            disabled={isSubmitting || isParentSubmitting}
          >
            Edit Section
          </Button>
          
          <Button
            variant="ghost"
            size="icon"
            type="button"
            onClick={() => onDeleteSection(section.id)}
            className="h-9 w-9 text-destructive hover:text-destructive/90"
            disabled={isSubmitting || isParentSubmitting}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      <div className="p-4 space-y-6">
        <div className="space-y-4">
          <h4 className="text-sm font-medium">Useful Links</h4>
          
          {usefulLinks.length > 0 ? (
            <>
              {usefulLinks.map((link, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-2 bg-gray-50 p-2 rounded"
                >
                  {isUploadedFile(link.url) ? (
                    <FileText className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Link className="h-4 w-4 text-muted-foreground" />
                  )}
                  <div className="flex-grow">
                    <p className="text-sm font-medium">{link.label}</p>
                    <a 
                      href={link.url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-xs text-blue-600 truncate"
                    >
                      {link.url}
                    </a>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    type="button"
                    onClick={() => removeUsefulLink(index)}
                    className="text-destructive hover:text-destructive/90"
                    disabled={isSubmitting}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </>
          ) : null}
          
          {isAddingLink ? (
            <div className="space-y-2">
              <div className="flex space-x-2">
                <div className="flex flex-1 items-center border rounded-md focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 bg-background">
                  <input
                    placeholder="URL or upload a file"
                    value={newLinkUrl}
                    onChange={(e) => handleUrlChange(e.target.value)}
                    onKeyDown={handleKeyPress}
                    disabled={isSubmitting || isUploading}
                    className="flex-1 px-3 py-2 text-sm bg-transparent outline-none disabled:opacity-50"
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileSelect}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.png,.jpg,.jpeg"
                    className="hidden"
                    disabled={isSubmitting || isUploading}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isSubmitting || isUploading}
                    className="px-3 text-muted-foreground hover:text-foreground disabled:opacity-50"
                    title="Upload file"
                  >
                    {isUploading ? (
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent inline-block"></span>
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <Input
                  placeholder="Label (auto-filled)"
                  value={newLinkLabel}
                  onChange={(e) => setNewLinkLabel(e.target.value)}
                  onKeyDown={handleKeyPress}
                  disabled={isSubmitting || isUploading}
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  size="icon"
                  type="button"
                  onClick={addUsefulLink}
                  disabled={isSubmitting || isUploading || !newLinkUrl.trim()}
                  title="Add link"
                >
                  {isSubmitting ? (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"></span>
                  ) : (
                    <PlusCircle className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={cancelAddLink}
                disabled={isSubmitting || isUploading}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={() => setIsAddingLink(true)}
              disabled={isSubmitting}
            >
              <PlusCircle className="h-4 w-4 mr-2" />
              Add Useful Link
            </Button>
          )}
        </div>

        <div className="space-y-4">
          <FormFieldComponent 
            section={section} 
            onFieldUpdated={onSectionUpdated}
            isParentSubmitting={isSubmitting || isParentSubmitting}
          />
          
          <Button
            variant="outline"
            size="sm"
            type="button"
            onClick={() => {
              console.log("Add Field button clicked, opening dialog");
              setIsDialogOpen(true);
            }}
            className="w-full relative"
            disabled={isDisabled}
            data-testid="add-field-button"
          >
            <PlusCircle className="h-4 w-4 mr-2" />
            Add Field
          </Button>
        </div>
      </div>
      
      <SimpleAddFieldDialog
        isOpen={isDialogOpen}
        onClose={() => {
          console.log("SimpleAddFieldDialog onClose fired");
          if (!isSubmitting) {
            setIsDialogOpen(false);
          }
        }}
        onAddField={handleAddField}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default FormSectionComponent;
